// externals
import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

// libraries
import { BreakPoint, logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// components
import { BaseDealContainerRootCompact, BaseDealContent, BaseDealVehicleInfo } from '../BaseDealActivity.interstate.style';
import { VehicleInfoCard } from '../../vehicleInfo/VehicleInfoCard';
import InterstateDealSummary from '../../dealSummary/DealSummary.interstate';
import { MultiScenarioActions } from './MultiScenarioActions';
import BreakdownTabsContainer from './Breakdown/BreakdownTabs/BreakdownTabsContainer.interstate';
import { AiInsightsCards } from './AiInsightsCards';
import { Box } from '@interstate/components/Box';
import { AiLogoIcon } from '../../common/AiLogoIcon';
import { AssistantConnectionDiagnostic } from './AssistantConnectionDiagnostic/AssistantConnectionDiagnostic';

// selectors
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { scopedStateSelectors, scopedActionsCreators } from '@makemydeal/shared-scoped-store';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// hooks
import { useAiInsights } from './hooks/useAiInsights';

// types
import { BaseDealDeskingCardSkeletonLoader } from './BaseDealDeskingCardSkeletonLoader';
import { AICardWrapper, AICardLinksWrapper } from './BaseDealContainer.style';
import BaseDealSummary from './BaseDealSummary.interstate';
import Breakdown from './Breakdown/Breakdown';
import { ConditionalProfiler, FsSyncFailureBanner } from '@makemydeal/dr-dash-components';

const BaseDealContainer = ({ scopeId = '' }: { scopeId?: string }) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();

    // Global Feature Toggles
    const isDealRecapTabEnabled = useSelector(featureToggleSelectors.useDealRecapEnabled);

    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const isPrimaryScope = primaryScopeId === scopeId;
    const isBreakDownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isTabletScreenSize = useMediaQuery({ query: `(max-width: ${BreakPoint.MEDIUM})` });
    const isSmallScreenSize = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` });
    const initializedScopes = useSelector(scopedStateSelectors.getInitializedScopesOrder);
    const isInitialized = initializedScopes.includes(scopeId);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);

    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const scopesOrder = useSelector(scopedStateSelectors.getScopesOrder);
    const isMultiScenario = scopesOrder.length >= 2;
    const isCondensedLayout = isMultiScenario || isSmallScreenSize;
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const isFsSyncFailureMessagingEnabled = useSelector(featureToggleSelectors.isFsSyncFailureMessagingEnabled);
    const isAssistantDiagnosticEnabled = useSelector(dealerSelectors.isInventoryAssistantEnabled);
    const focusedScopeId = useSelector(scopedStateSelectors.getFocusedScopeId);

    const profilerId = useMemo(() => {
        if (isPrimaryScope) return 'base-deal-primary';
        const secondaryIndex = initializedScopes.filter((id: string) => id !== primaryScopeId).indexOf(scopeId);
        return `base-deal-scenario-${secondaryIndex + 1}`;
    }, [isPrimaryScope, initializedScopes, primaryScopeId, scopeId]);

    const hasRecordedPaintRef = useRef(false);
    useEffect(() => {
        if (!isInitialized || isPrimaryScope || hasRecordedPaintRef.current) return;
        hasRecordedPaintRef.current = true;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                logNewRelicPageAction('MV:AlternateScenarioPainted', {
                    duration: performance.now()
                });
            });
        });
    }, [isInitialized, isPrimaryScope]);

    const aiInsights = useAiInsights({ scopeId });

    const fsSyncFailureBanner = isFnIDealMode && isFsSyncFailureMessagingEnabled ? <FsSyncFailureBanner /> : null;

    if (isTabletScreenSize && isBreakDownOpen && !isDealCentralExperience) {
        return (
            <ConditionalProfiler id={profilerId}>
                <BaseDealContainerRootCompact
                    theme={theme}
                    className={isPrimaryScope && isDealCentralMultiScenario ? 'selected' : ''}
                    isDealCentralMultiScenario={isDealCentralMultiScenario}
                    isDealCentralExperience={isDealCentralExperience}
                    data-testid={`deal-content-card-${scopeId}`}
                    data-scope-container={true}
                >
                    {fsSyncFailureBanner}
                    {isDealRecapTabEnabled ? <BreakdownTabsContainer scopeId={scopeId} isFullWidth /> : <InterstateDealSummary />}
                </BaseDealContainerRootCompact>
            </ConditionalProfiler>
        );
    }

    if (scopeId && !isInitialized) {
        return (
            <ConditionalProfiler id={profilerId}>
                <BaseDealContainerRootCompact
                    theme={theme}
                    className={isPrimaryScope && isDealCentralMultiScenario ? 'selected' : ''}
                    isDealCentralMultiScenario={isDealCentralMultiScenario}
                    isDealCentralExperience={isDealCentralExperience}
                    data-testid={`deal-content-card-${scopeId}`}
                    data-scope-container={true}
                >
                    <BaseDealDeskingCardSkeletonLoader />
                </BaseDealContainerRootCompact>
            </ConditionalProfiler>
        );
    }

    const handleFocus = () => {
        if (scopeId && focusedScopeId !== scopeId) {
            dispatch(scopedActionsCreators.setFocusedScopeId(scopeId));
        }
    };

    return (
        <ConditionalProfiler id={profilerId}>
            <BaseDealContainerRootCompact
                theme={theme}
                className={isPrimaryScope && isDealCentralMultiScenario ? 'selected' : ''}
                isDealCentralMultiScenario={isDealCentralMultiScenario}
                isDealCentralExperience={isDealCentralExperience}
                data-testid={`deal-content-card-${scopeId}`}
                data-scope-container={true}
                onFocus={handleFocus}
                onClick={handleFocus}
            >
                {/* Breakdown on the LEFT - only when BASE DEAL SCREEN (isDealCentralExperience is FALSE) */}
                {!isDealCentralExperience && !isTabletScreenSize && (
                    <Box mr={2}>
                        <Breakdown scopeId={scopeId} isLightMode alwaysVisible />
                    </Box>
                )}
                <BaseDealContent isDealCentralMultiScenario={isDealCentralMultiScenario}>
                    {fsSyncFailureBanner}
                    {scopeId && !isFnIDealMode && <MultiScenarioActions scopeId={scopeId} />}
                    {aiInsights.showAISection && (
                        <AICardWrapper>
                            {isCondensedLayout && (
                                <AiLogoIcon data-testid="ai-section-shared-logo" fontSize="20px" ariaLabel="AI insights" />
                            )}
                            <AICardLinksWrapper isCondensedLayout={isCondensedLayout}>
                                <AiInsightsCards scopeId={scopeId} insights={aiInsights} isCondensedLayout={isCondensedLayout} />
                            </AICardLinksWrapper>
                        </AICardWrapper>
                    )}
                    {isAssistantDiagnosticEnabled && (!scopeId || isPrimaryScope) && <AssistantConnectionDiagnostic />}

                    <BaseDealVehicleInfo isDealCentralMultiScenario={isDealCentralMultiScenario}>
                        <VehicleInfoCard />
                    </BaseDealVehicleInfo>
                    <Box sx={{ mt: 1 }}>
                        <BaseDealSummary scopeId={scopeId} />
                    </Box>
                </BaseDealContent>
            </BaseDealContainerRootCompact>
        </ConditionalProfiler>
    );
};

export default BaseDealContainer;
